import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Hero } from "@/components/site/Hero";
import { Marquee } from "@/components/site/Marquee";
import { Impact } from "@/components/site/Impact";
import { Reviews } from "@/components/site/Reviews";
import { QuoteFooter } from "@/components/site/QuoteFooter";
import { IMG, CATEGORIES, HOME_STATS } from "@/components/site/data";
import { Reveal, scrollToId } from "@/components/site/motion";
import { Seo, organizationLd, localBusinessLd } from "@/components/site/Seo";

const CategoryChooser = () => (
  <section id="categories" data-testid="categories-section" className="relative py-28 lg:py-40 bg-[#010101]">
    <div className="max-w-7xl mx-auto px-6 lg:px-12">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-16">
        <div>
          <Reveal><p className="text-xs tracking-[0.3em] uppercase text-[#fcc41b] font-display font-medium mb-5">Two ways we harness the sun</p></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display font-semibold tracking-tighter leading-[0.95] text-white" style={{ fontSize: "clamp(2.25rem, 5.5vw, 4.5rem)" }}>
              Choose your<br />solution.
            </h2>
          </Reveal>
        </div>
        <Reveal delay={0.1}><p className="text-zinc-400 max-w-sm text-base leading-relaxed">Explore our complete range of solar hot water and solar roof ventilation systems for residential, commercial and industrial applications.</p></Reveal>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {CATEGORIES.map((c, i) => (
          <Reveal key={c.id} delay={i * 0.1}>
            <Link
              to={c.to}
              data-testid={`category-card-${c.id}`}
              className="group relative block rounded-3xl overflow-hidden border border-[#262626] hover:border-[#fcc41b]/40 transition-colors duration-500 h-[460px]"
            >
              <img src={c.image} alt={c.title} className="grayscale-img absolute inset-0 w-full h-full object-cover group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#010101] via-[#010101]/40 to-transparent" />
              <div className="relative z-10 h-full flex flex-col justify-end p-8 lg:p-10">
                <p className="font-mono-eng text-xs tracking-wider text-[#fcc41b] mb-3">{c.kicker}</p>
                <h3 className="font-display font-semibold text-3xl lg:text-4xl text-white mb-4">{c.title}</h3>
                <p className="text-zinc-300 leading-relaxed max-w-md mb-6">{c.blurb}</p>
                <span className="inline-flex items-center gap-2 text-[#fcc41b] font-display font-medium">
                  Explore range
                  <ArrowUpRight size={18} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300" />
                </span>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Seo
        path="/"
        description="Australian manufacturer of high-performance solar hot water systems and solar roof ventilation. Trusted by 30,000+ homes and businesses since 2009. Request a free quote."
        jsonLd={[organizationLd, localBusinessLd]}
      />
      <Hero
        overline="SolarArk Australia · Est. 2009"
        line1="Engineered for"
        line2Prefix="the"
        line2Highlight="Australian sun."
        subtitle={<>High-performance <span className="text-white font-semibold">solar hot water</span> and <span className="text-white font-semibold">solar roof ventilation</span> trusted by over 30,000 Australian homes and businesses to cut energy costs and improve comfort.</>}
        bgImage={IMG.ventHero}
        secondaryLabel="Explore Solutions"
        onSecondary={() => scrollToId("categories")}
      />
      <Marquee />
      <CategoryChooser />
      <Impact overline="National Impact" title="Fifteen years of Australian solar engineering." stats={HOME_STATS} />
      <Reviews />
      <QuoteFooter />
    </>
  );
}
